import { Stack } from '@startuptools/ui/Stack';
import { ComponentProps, Children, Suspense, useRef, useEffect, ReactElement } from 'react';
import { Sheet } from '@startuptools/ui/Sheet';
import { Route, Routes, useLocation, useNavigate } from 'react-router-dom';
import { StepperProvider } from '../common/StepperContext';
import { compact, isArray } from 'lodash-es';
import { StepperStepProvider } from '../common/StepperStepContext';
import { useOnce } from '@startuptools/common/react-hooks';
import { StepperStepContentProvider } from '../common/StepperStepContentContext';
import { Typography } from '@startuptools/ui/joy/Typography';
import { NavStepperStep } from './NavStepperStep';

export type Child = ReactElement<ComponentProps<typeof NavStepperStep>> | false | undefined;

const getChildrenArray = (children: Child | Child[]) => {
  if (!children) return [];
  return isArray(children) ? compact(children) : [children];
};

export const NavStepper = ({
  path,
  children,
  onStepSaved,
}: {
  path: string;
  children: Child | Child[];
  onStepSaved: () => Promise<unknown> | unknown;
}) => {
  const location = useLocation();
  const navigate = useNavigate();
  const childrenRef = useRef(children);

  useEffect(() => {
    childrenRef.current = children;
  }, [children]);

  useOnce(() => {
    const childrenArr = getChildrenArray(children);
    if (childrenArr.length === 0) {
      return;
    }
    const currentPath = location.pathname;
    const needsToNavigate = !childrenArr.some(c => c && currentPath.endsWith(c.props.path));
    if (needsToNavigate) {
      void navigate(`${currentPath}/${childrenArr[0]!.props.path}`, { replace: true });
    }
  });

  if (!children) {
    return null;
  }

  const stepTo = (step: string) => {
    if (!step) {
      return;
    }
    const currentPath = location.pathname;
    const nextPath = currentPath.replace(/\/[\w-_]+\/?$/, `/${step}`);
    return navigate(nextPath, { replace: true });
  };

  const stepNext = async (onLastStep?: () => Promise<unknown>) => {
    await onStepSaved();
    const childrenArr = getChildrenArray(childrenRef.current); // read latest children from ref, rather than instance from render cycle
    const currentPath = location.pathname;
    const currentChildIdx = childrenArr.findIndex(c => currentPath.endsWith(c.props.path));
    const nextChild = childrenArr[currentChildIdx + 1];
    if (nextChild) {
      const nextPath = currentPath.replace(/\/[\w-_]+\/?$/, `/${nextChild.props.path}`);
      return navigate(nextPath, { replace: true });
    } else if (onLastStep) {
      return onLastStep();
    }
  };

  const childrenArr = getChildrenArray(children);

  return (
    <Routes>
      <Route
        path={path}
        element={
          <Stack direction="row" gap={2}>
            <Stack flexGrow={0}>
              <Sheet variant="outlined" sx={{ borderRadius: 6, paddingY: 1.5 }}>
                <Stack direction="column">
                  {Children.map(childrenArr, (c, i) => (
                    <StepperStepProvider stepNumber={`${i + 1}`}>{c}</StepperStepProvider>
                  ))}
                </Stack>
              </Sheet>
            </Stack>

            <Sheet variant="outlined" sx={{ width: '100%', borderRadius: 6, padding: 2 }}>
              <StepperProvider stepNext={stepNext} stepTo={stepTo} currentStep="">
                <Routes>
                  {Children.map(childrenArr, (c, i) => {
                    return (
                      <Route
                        key={i}
                        path={c.props.path}
                        index={c.props.index}
                        element={
                          <StepperStepContentProvider validatorStatus={c.props.validatorStatus}>
                            <Suspense>
                              <Stack gap={2}>
                                {c.props.showTitle && <Typography level="h4">{c.props.label}</Typography>}
                                {typeof c.props.children === 'function'
                                  ? c.props.children({ stepNext, stepTo, currentStep: '' })
                                  : c.props.children}
                              </Stack>
                            </Suspense>
                          </StepperStepContentProvider>
                        }
                      />
                    );
                  })}
                </Routes>
              </StepperProvider>
            </Sheet>
          </Stack>
        }
      />
    </Routes>
  );
};
